import React, { useEffect, useMemo, useState } from "react"
import {
  FaTimes,
  FaBuilding,
  FaFileInvoice,
  FaMapMarkerAlt,
  FaToggleOn,
  FaToggleOff,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaTrash,
  FaPlus,
  FaFile,
  FaPaperclip,
  FaUniversity,
  FaIdCard,
} from "react-icons/fa"
import { Vendor, VendorRepresentative } from "./index"
import { SearchableSelect, SelectWithSearch } from "../../utils/select-with-search"
import { countries } from "../../utils/countries"
import { CountryOption, DesignationOption } from "./add"
import axiosInstance from "../../libs/axios"
import { uploadFilesWithPresignedPayload } from "../../libs/presigned-upload"
import toast from "react-hot-toast"

interface VendorDocument {
  name: string
  url: string
  size?: number
  mime_type?: string
}

interface EditVendorFormData {
  id: number
  vendor_full_name: string
  vendor_short_name: string
  vendor_code?: string
  address: string
  gst_number: string
  state_circle_id: number
  vendor_type?: string
  is_active: boolean
  pan_number?: string
  bank_account_number?: string
  bank_ifsc_code?: string
  bank_name?: string
  bank_address?: string
  representatives?: VendorRepresentative[]
  project_ids?: number[]
  activity_ids?: number[]
  documents?: VendorDocument[]
}

interface EditVendorOffcanvasProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: EditVendorFormData) => void
  vendor: Vendor | null
  loading?: boolean
}

const initialFormData: EditVendorFormData = {
  id: 0,
  vendor_full_name: "",
  vendor_short_name: "",
  vendor_code: "",
  address: "",
  gst_number: "",
  state_circle_id: 0,
  vendor_type: "FREELANCE",
  is_active: true,
  pan_number: "",
  bank_account_number: "",
  bank_ifsc_code: "",
  bank_name: "",
  bank_address: "",
  representatives: [],
  project_ids: [],
  activity_ids: [],
}

export const EditVendorOffcanvas: React.FC<EditVendorOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  vendor,
  loading = false,
}) => {
  const [formData, setFormData] = useState<EditVendorFormData>(initialFormData)
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [representativeErrors, setRepresentativeErrors] = useState<Record<number, Partial<VendorRepresentative>>>({})
  const [selectedStateCircle, setSelectedStateCircle] = useState<any | null>(null)
  const [designationOptions, setDesignationOptions] = useState<DesignationOption[]>([]);
  const [designationLoading, setDesignationLoading] = useState(false);
  const [designationPage, setDesignationPage] = useState(1);
  const [designationHasMore, setDesignationHasMore] = useState(true);
  const [designationSearch, setDesignationSearch] = useState("");
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<Record<number, any | null>>({})
  const [selectedDesignations, setSelectedDesignations] = useState<Record<number, any | null>>({})
  const [selectedProjects, setSelectedProjects] = useState<{ value: string | number; label: string }[]>([])
  const [selectedActivities, setSelectedActivities] = useState<{ value: string | number; label: string }[]>([])
  const [projects, setProjects] = useState<{ id: number; project_name: string }[]>([])
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [activities, setActivities] = useState<{ id: number; activity_name: string }[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)
  const [existingDocuments, setExistingDocuments] = useState<VendorDocument[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)

  // Convert countries to SelectOption format
  const countryOptions = countries.map((country) => ({
    value: country.code,
    label: `${country.flag} ${country.code}`,
    flag: country.flag
  }))

  const fetchProjects = async () => {
    setProjectsLoading(true)
    try {
      const response = await axiosInstance.get('/projects/list', { params: { page: 1, limit: 200, status: 'ACTIVE' } })
      if (response.data.success) setProjects(response.data.data.projects || [])
    } catch { } finally { setProjectsLoading(false) }
  }

  const fetchActivities = async () => {
    setActivitiesLoading(true)
    try {
      const response = await axiosInstance.get('/activities/list', { params: { page: 1, limit: 200 } })
      if (response.data.success) setActivities(response.data.data.activities || [])
    } catch { } finally { setActivitiesLoading(false) }
  }

  const fetchDesignations = async (search: string = "", page: number = 1, append: boolean = false) => {
    setDesignationLoading(true);
    try {
      const response = await axiosInstance.get('/designations/list', {
        params: {
          search: search.trim() || undefined,
          page,
          limit: 20,
          status: 'active'
        }
      });

      if (response.data.success) {
        const newDesignations = response.data.data.designations.map((d: any) => ({
          value: d.id,
          label: d.name
        }));

        if (append) {
          setDesignationOptions(prev => [...prev, ...newDesignations]);
        } else {
          setDesignationOptions(newDesignations);
        }

        setDesignationHasMore(response.data.data.pagination.has_next_page);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    } finally {
      setDesignationLoading(false);
    }
  };

  const handleDesignationSearch = (search: string) => {
    setDesignationSearch(search);
    setDesignationPage(1);
    fetchDesignations(search, 1, false);
  };

  const updateRepresentativeDesignation = (index: number, option: DesignationOption | null) => {
    updateRepresentative(index, "designation_id", option ? option.value : null);
    setSelectedDesignations(prev => ({
      ...prev,
      [index]: option
    }));
  };

  const updateRepresentativeCountryCode = (index: number, option: CountryOption | null) => {
    if (option) {
      updateRepresentative(index, "country_code", option.value);
      setSelectedCountryCodes(prev => ({
        ...prev,
        [index]: option
      }));
    }
  };

  // Initialize all data when vendor is available and offcanvas opens
  useEffect(() => {
    if (vendor && isOpen) {
      fetchProjects()
      fetchActivities()
      // Fetch designations first
      fetchDesignations().then(() => {
        // Set form data after designations are loaded
        const representatives = vendor.representatives || [];

        setFormData({
          id: vendor.id,
          vendor_full_name: vendor.vendor_full_name || "",
          vendor_short_name: vendor.vendor_short_name || "",
          vendor_code: vendor.vendor_code || "",
          address: vendor.address || "",
          gst_number: vendor.gst_number || "",
          state_circle_id: vendor.state_circle_id || 0,
          vendor_type: vendor.vendor_type || "FREELANCE",
          is_active: vendor.is_active,
          pan_number: (vendor as any).pan_number || "",
          bank_account_number: (vendor as any).bank_account_number || "",
          bank_ifsc_code: (vendor as any).bank_ifsc_code || "",
          bank_name: (vendor as any).bank_name || "",
          bank_address: (vendor as any).bank_address || "",
          representatives: representatives,
          project_ids: (vendor.project_associations || []).map((pa) => pa.project_id),
          activity_ids: (vendor.activity_associations || []).map((aa: any) => aa.activity_id),
        });

        // Populate project multi-select
        setSelectedProjects(
          (vendor.project_associations || []).map((pa) => ({
            value: pa.project_id,
            label: pa.project?.project_name || String(pa.project_id),
          }))
        );

        // Populate activities multi-select
        setSelectedActivities(
          (vendor.activity_associations || []).map((aa: any) => ({
            value: aa.activity_id,
            label: aa.activity?.activity_name || String(aa.activity_id),
          }))
        );

        // Set selected state circle
        if (vendor.state_circle_id) {
          setSelectedStateCircle({
            value: vendor.state_circle_id,
            label: `State Circle ${vendor.state_circle_id}`
          });
        }

        // Set selected country codes and designations for representatives
        const countryCodes: Record<number, any> = {};
        const designations: Record<number, any> = {};

        representatives.forEach((rep, index) => {
          // Set country code
          if (rep.country_code) {
            const countryOption = countryOptions.find(c => c.value === rep.country_code);
            if (countryOption) {
              countryCodes[index] = countryOption;
            }
          }

          // Set designation - will be updated when designations load
          if (rep.designation_id) {
            const designationOption = designationOptions.find(d => d.value === rep.designation_id);
            if (designationOption) {
              designations[index] = designationOption;
            }
          }
        });

        setSelectedCountryCodes(countryCodes);
        setSelectedDesignations(designations);
      });

      setExistingDocuments((vendor as any).documents || []);
      setPendingFiles([]);

      setErrors({});
      setTouched({});
      setRepresentativeErrors({});
    }
  }, [vendor, isOpen]);

  // Update designations when options load and vendor has representatives
  useEffect(() => {
    if (vendor && vendor.representatives && designationOptions.length > 0) {
      setSelectedDesignations(prev => {
        const designations = { ...prev };
        
        if (vendor && vendor.representatives) {
          vendor.representatives.forEach((rep, index) => {
            if (rep.designation_id) {
              const designationOption = designationOptions.find(d => d.value === rep.designation_id);
              if (designationOption) {
                designations[index] = designationOption;
              }
            }
          });
        }
        
        return designations;
      });
    }
  }, [designationOptions, vendor]);

  const handleStateCircleChange = (option: any | null) => {
    setSelectedStateCircle(option)
    setFormData((prev) => ({
      ...prev,
      state_circle_id: option ? Number(option.value) : 0,
    }))
    if (errors.state_circle_id) {
      setErrors((prev) => ({
        ...prev,
        state_circle_id: null,
      }))
    }
  }

  const validateField = (name: string, value: string) => {
    // Safe trim to fix the TypeError
    const trimmedValue = value ? value.trim() : "";
    
    switch (name) {
      case "vendor_full_name":
        if (!trimmedValue) return "Full name is required"
        if (trimmedValue.length < 2) return "Full name must be at least 2 characters"
        if (trimmedValue.length > 100) return "Full name must be less than 100 characters"
        break
      case "vendor_short_name":
        if (!trimmedValue) return "Short name is required"
        if (trimmedValue.length < 2) return "Short name must be at least 2 characters"
        if (trimmedValue.length > 50) return "Short name must be less than 50 characters"
        break
      case "address":
        if (!trimmedValue) return "Address is required"
        if (trimmedValue.length < 10) return "Address must be at least 10 characters"
        if (trimmedValue.length > 500) return "Address must be less than 500 characters"
        break
      case "gst_number":
        if (trimmedValue && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(trimmedValue)) return "Invalid GST number format"
        break
      case "state_circle_id":
        if (!value || Number.isNaN(Number(value))) return "State circle is required"
        break
      default:
        break
    }
    return null
  }

  const validateRepresentative = (rep: VendorRepresentative) => {
    const repErrors: Partial<VendorRepresentative> = {}

    // Safe trim checks
    const contactPerson = rep.contact_person ? rep.contact_person.trim() : "";
    const contactNumber = rep.contact_number ? rep.contact_number.trim() : "";
    const location = rep.representative_location ? rep.representative_location.trim() : "";
    const email = rep.email ? rep.email.trim() : "";

    if (!contactPerson) {
      repErrors.contact_person = "Contact person is required"
    } else if (contactPerson.length < 2) {
      repErrors.contact_person = "Name must be at least 2 characters"
    }

    if (!contactNumber) {
      repErrors.contact_number = "Contact number is required"
    } else if (contactNumber.length < 6 || contactNumber.length > 15) {
      repErrors.contact_number = "Enter a valid phone number"
    }

    if (!rep.designation_id) {
      repErrors.designation_id = "Designation is required" as any;
    }

    if (!location) {
      repErrors.representative_location = "Location is required"
    }

    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      repErrors.email = "Enter a valid email"
    }

    return repErrors
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target

    setFormData((prev) => ({
      ...prev,
      [name]: name === "state_circle_id" ? Number(value) : value,
    }))

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }))
    }

    if (touched[name]) {
      const error = validateField(name, value)
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }))
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }))

    const error = validateField(name, value)
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }))
  }

  const toggleStatus = () => {
    setFormData((prev) => ({
      ...prev,
      is_active: !prev.is_active,
    }))
  }

  const addRepresentativeField = () => {
    const newIndex = formData.representatives?.length || 0;
    
    setFormData((prev) => ({
      ...prev,
      representatives: [
        ...(prev.representatives || []),
        {
          contact_person: "",
          contact_number: "",
          country_code: "+91",
          designation: "",
          representative_location: "",
          email: "",
          designation_id: null
        },
      ],
    }))

    // Set default country code for new representative
    const defaultCountry = countryOptions.find(c => c.value === "+91") || countryOptions[0];
    setSelectedCountryCodes(prev => ({
      ...prev,
      [newIndex]: defaultCountry
    }));
  }

  const removeRepresentativeField = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives?.filter((_, i) => i !== index) || [],
    }))

    setRepresentativeErrors((prev) => {
      const nextErrors = { ...prev }
      delete nextErrors[index]
      return nextErrors
    })

    setSelectedCountryCodes(prev => {
      const next = { ...prev }
      delete next[index]
      return next
    })

    setSelectedDesignations(prev => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const updateRepresentative = (index: number, field: keyof VendorRepresentative, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      representatives: prev.representatives?.map((rep, i) =>
        i === index ? { ...rep, [field]: value } : rep,
      ) || [],
    }))

    if (representativeErrors[index]?.[field]) {
      setRepresentativeErrors((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: undefined,
        },
      }))
    }
  }

  const validateRepresentatives = () => {
    const errors: Record<number, Partial<VendorRepresentative>> = {}
    let hasErrors = false

    formData.representatives?.forEach((rep, index) => {
      const repErrors = validateRepresentative(rep)
      if (Object.keys(repErrors).length > 0) {
        errors[index] = repErrors
        hasErrors = true
      }
    })

    setRepresentativeErrors(errors)
    return !hasErrors
  }

  const hasChanges = () => {
    if (!vendor) return false

    const vendorFullNameChanged = formData.vendor_full_name.trim() !== (vendor.vendor_full_name || "").trim()
    const vendorShortNameChanged = formData.vendor_short_name.trim() !== (vendor.vendor_short_name || "").trim()
    const vendorCodeChanged = (formData.vendor_code || "").trim() !== (vendor.vendor_code || "").trim()
    const addressChanged = formData.address.trim() !== (vendor.address || "").trim()
    const gstNumberChanged = formData.gst_number.trim() !== (vendor.gst_number || "").trim()
    const stateCircleChanged = formData.state_circle_id !== vendor.state_circle_id
    const vendorTypeChanged = (formData.vendor_type || "FREELANCE") !== (vendor.vendor_type || "FREELANCE")
    const statusChanged = formData.is_active !== vendor.is_active
    const bankChanged =
      (formData.bank_account_number || "") !== ((vendor as any).bank_account_number || "") ||
      (formData.bank_ifsc_code || "") !== ((vendor as any).bank_ifsc_code || "") ||
      (formData.bank_name || "") !== ((vendor as any).bank_name || "") ||
      (formData.bank_address || "") !== ((vendor as any).bank_address || "") ||
      (formData.pan_number || "") !== ((vendor as any).pan_number || "")

    const currentReps = formData.representatives || [];
    const vendorReps = vendor.representatives || [];
    const repsChanged = JSON.stringify(currentReps) !== JSON.stringify(vendorReps);

    const originalProjectIds = (vendor.project_associations || []).map((pa) => pa.project_id).sort((a, b) => a - b)
    const currentProjectIds = selectedProjects.map((p: any) => Number(p.value)).sort((a, b) => a - b)
    const projectsChanged = JSON.stringify(originalProjectIds) !== JSON.stringify(currentProjectIds)

    const originalActivityIds = (vendor.activity_associations || []).map((aa: any) => aa.activity_id).sort((a: number, b: number) => a - b)
    const currentActivityIds = selectedActivities.map((a: any) => Number(a.value)).sort((a: number, b: number) => a - b)
    const activityChanged = JSON.stringify(originalActivityIds) !== JSON.stringify(currentActivityIds)

    const originalDocCount = ((vendor as any).documents || []).length
    const docsChanged = existingDocuments.length !== originalDocCount || pendingFiles.length > 0

    return vendorFullNameChanged || vendorShortNameChanged || vendorCodeChanged || addressChanged ||
           gstNumberChanged || stateCircleChanged || vendorTypeChanged || statusChanged || repsChanged ||
           projectsChanged || activityChanged || docsChanged || bankChanged;
  }

  const validateForm = () => {
    const newErrors: Record<string, string | null> = {}
    let isValid = true

    const fieldsToValidate = ["vendor_full_name", "vendor_short_name", "address", "gst_number", "state_circle_id"]
    fieldsToValidate.forEach((field) => {
      const value = formData[field as keyof EditVendorFormData]
      const error = validateField(field, String(value ?? ""))
      if (error) {
        newErrors[field] = error
        isValid = false
      }
    })

    setErrors(newErrors)
    setTouched({
      vendor_full_name: true,
      vendor_short_name: true,
      address: true,
      gst_number: true,
      state_circle_id: true,
    })

    if (!validateRepresentatives()) {
      isValid = false
    }

    return isValid
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    if (!hasChanges()) {
      onClose()
      return
    }

    let uploadedDocs: VendorDocument[] = []
    if (pendingFiles.length > 0) {
      setUploading(true)
      try {
        const results = await uploadFilesWithPresignedPayload(pendingFiles, { folderName: 'vendors/documents' })
        uploadedDocs = results.map((r) => ({
          name: r.fileName,
          url: r.fileUrl,
          size: r.fileSize,
          mime_type: r.mimeType,
        }))
      } catch {
        toast.error('Failed to upload documents. Please try again.')
        setUploading(false)
        return
      }
      setUploading(false)
    }

    const payload: EditVendorFormData = {
      id: formData.id,
      vendor_full_name: formData.vendor_full_name.trim(),
      vendor_short_name: formData.vendor_short_name.trim(),
      vendor_code: formData.vendor_code?.trim() || undefined,
      address: formData.address.trim(),
      gst_number: formData.gst_number.trim().toUpperCase(),
      state_circle_id: formData.state_circle_id,
      vendor_type: formData.vendor_type,
      is_active: formData.is_active,
      pan_number: formData.pan_number?.trim() || undefined,
      bank_account_number: formData.bank_account_number?.trim() || undefined,
      bank_ifsc_code: formData.bank_ifsc_code?.trim().toUpperCase() || undefined,
      bank_name: formData.bank_name?.trim() || undefined,
      bank_address: formData.bank_address?.trim() || undefined,
      project_ids: selectedProjects.map((p: any) => Number(p.value)),
      activity_ids: selectedActivities.map((a: any) => Number(a.value)),
      documents: [...existingDocuments, ...uploadedDocs],
      representatives: formData.representatives?.map((rep) => ({
        ...rep,
        contact_person: rep.contact_person ? rep.contact_person.trim() : "",
        contact_number: rep.contact_number ? rep.contact_number.trim() : "",
        country_code: rep.country_code,
        designation: rep.designation ? rep.designation.trim() : "",
        designation_id: rep.designation_id,
        representative_location: rep.representative_location ? rep.representative_location.trim() : "",
        email: rep.email ? rep.email.trim() : undefined,
      })),
    }

    onSubmit(payload)
  }

  const handleClose = () => {
    if (!loading && !uploading) {
      setExistingDocuments([])
      setPendingFiles([])
      onClose()
    }
  }

  const formatBytes = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (!isOpen || !vendor) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleClose} />
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-4xl bg-white shadow-xl z-50 overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Edit Vendor</h2>
            <p className="text-sm text-gray-600 mt-1">Last updated: {new Date(vendor.updated_at).toLocaleString()}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="h-full overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded p-6 space-y-6">
              <div className="flex items-center gap-2">
                <FaBuilding className="text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">Vendor Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="vendor_full_name"
                    value={formData.vendor_full_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter vendor full name"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.vendor_full_name && touched.vendor_full_name
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300"
                      }`}
                    disabled={loading}
                  />
                  {errors.vendor_full_name && touched.vendor_full_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.vendor_full_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="vendor_short_name"
                    value={formData.vendor_short_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter vendor short name"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.vendor_short_name && touched.vendor_short_name
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300"
                      }`}
                    disabled={loading}
                  />
                  {errors.vendor_short_name && touched.vendor_short_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.vendor_short_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Code</label>
                  <input
                    type="text"
                    name="vendor_code"
                    value={formData.vendor_code || ""}
                    onChange={handleInputChange}
                    placeholder="e.g. VND-001"
                    maxLength={50}
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    disabled={loading}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    rows={3}
                    placeholder="Enter complete address"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.address && touched.address ? "border-red-500 bg-red-50" : "border-gray-300"
                      }`}
                    disabled={loading}
                  />
                  {errors.address && touched.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaFileInvoice className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="gst_number"
                      value={formData.gst_number}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={15}
                      placeholder="e.g. 27AAAAA0000A1Z5"
                      className={`w-full pl-10 pr-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono ${errors.gst_number && touched.gst_number ? "border-red-500 bg-red-50" : "border-gray-300"
                        }`}
                      disabled={loading}
                    />
                  </div>
                  {errors.gst_number && touched.gst_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.gst_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State Circle <span className="text-red-500">*</span>
                  </label>
                  <SelectWithSearch
                    value={selectedStateCircle}
                    onChange={handleStateCircleChange}
                    placeholder="Select state"
                    apiEndpoint="/state-circle/list"
                    dataKey="states"
                    labelKey="state_name"
                    displayFormat={(item) => `${item.state_name}`}
                  />
                  {errors.state_circle_id && touched.state_circle_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.state_circle_id}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.vendor_type || "FREELANCE"}
                  onChange={(e) => setFormData((prev) => ({ ...prev, vendor_type: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={loading}
                >
                  <option value="FREELANCE">Freelance</option>
                  <option value="FREELANCE_GROUP">Freelance Group</option>
                  <option value="FIRM">Firm</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleStatus}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium ${formData.is_active
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                    }`}
                  disabled={loading}
                >
                  {formData.is_active ? (
                    <>
                      <FaToggleOn className="w-5 h-5" /> Active
                    </>
                  ) : (
                    <>
                      <FaToggleOff className="w-5 h-5" /> Suspended
                    </>
                  )}
                </button>
                {vendor.suspended_at && !formData.is_active && (
                  <p className="text-xs text-gray-500">
                    Suspended on: {new Date(vendor.suspended_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* ===== Bank Details ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6 space-y-5">
              <div className="flex items-center gap-2">
                <FaUniversity className="text-blue-800" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Bank Details</h3>
                  <p className="text-xs text-gray-500 mt-0.5">All fields optional</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaIdCard className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="pan_number"
                      value={formData.pan_number || ""}
                      onChange={handleInputChange}
                      placeholder="e.g. ABCDE1234F"
                      maxLength={10}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Account Number</label>
                  <input
                    type="text"
                    name="bank_account_number"
                    value={formData.bank_account_number || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, bank_account_number: e.target.value.replace(/[^0-9]/g, "") }))}
                    placeholder="Enter account number"
                    maxLength={20}
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank IFSC Code</label>
                  <input
                    type="text"
                    name="bank_ifsc_code"
                    value={formData.bank_ifsc_code || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, bank_ifsc_code: e.target.value.toUpperCase() }))}
                    placeholder="e.g. SBIN0001234"
                    maxLength={11}
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name || ""}
                    onChange={handleInputChange}
                    placeholder="e.g. State Bank of India"
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Address</label>
                  <textarea
                    name="bank_address"
                    value={formData.bank_address || ""}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Enter bank branch address"
                    className="w-full px-3 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* ===== Project & Activity ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6 space-y-5">
              <div className="flex items-center gap-2">
                <FaBuilding className="text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Project & Activity</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Optional associations</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Projects</label>
                  <SearchableSelect
                    value={selectedProjects}
                    isMulti={true}
                    showSelectAllVisible={true}
                    onChange={(options: any) => setSelectedProjects(options || [])}
                    options={projects.map(p => ({ value: p.id, label: p.project_name }))}
                    placeholder="Search and select projects"
                    disabled={loading}
                    isLoading={projectsLoading}
                    minSearchLength={3}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Activities</label>
                  <SearchableSelect
                    value={selectedActivities}
                    isMulti={true}
                    showSelectAllVisible={true}
                    onChange={(options: any) => setSelectedActivities(options || [])}
                    options={activities.map(a => ({ value: a.id, label: a.activity_name }))}
                    placeholder="Search and select activities"
                    disabled={loading}
                    isLoading={activitiesLoading}
                    minSearchLength={3}
                  />
                </div>
              </div>
            </div>

            {/* ===== Documents ===== */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaPaperclip className="text-blue-600" />
                  <h3 className="text-lg font-medium text-gray-900">Documents</h3>
                </div>
                <label className="cursor-pointer px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors flex items-center gap-2">
                  <FaPlus className="w-3 h-3" />
                  Add Files
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    disabled={loading || uploading}
                    onChange={(e) => {
                      if (e.target.files) {
                        setPendingFiles(prev => [...prev, ...Array.from(e.target.files!)])
                        e.target.value = ''
                      }
                    }}
                  />
                </label>
              </div>

              {existingDocuments.length === 0 && pendingFiles.length === 0 ? (
                <p className="text-sm text-gray-500">No documents uploaded yet.</p>
              ) : (
                <div className="space-y-1">
                  {existingDocuments.map((doc, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded">
                      <FaFile className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="flex-1 text-sm text-gray-800 truncate">{doc.name}</span>
                      {doc.size && <span className="text-xs text-gray-500 shrink-0">{formatBytes(doc.size)}</span>}
                      <button
                        type="button"
                        onClick={() => setExistingDocuments(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700 p-0.5 shrink-0"
                        disabled={loading || uploading}
                        title="Remove"
                      >
                        <FaTimes className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {pendingFiles.map((file, idx) => (
                    <div key={`pending-${idx}`} className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
                      <FaFile className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className="flex-1 text-sm text-blue-800 truncate">{file.name}</span>
                      <span className="text-xs text-blue-500 shrink-0">{formatBytes(file.size)}</span>
                      <span className="text-xs text-blue-500 italic shrink-0">new</span>
                      <button
                        type="button"
                        onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700 p-0.5 shrink-0"
                        disabled={loading || uploading}
                        title="Remove"
                      >
                        <FaTimes className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaUser className="text-blue-600" />
                  <h3 className="text-lg font-medium text-gray-900">Representatives</h3>
                </div>
                <button
                  type="button"
                  onClick={addRepresentativeField}
                  className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
                  disabled={loading}
                >
                  <FaPlus className="w-3 h-3" />
                  Add Representative
                </button>
              </div>

              {(formData.representatives || []).length === 0 ? (
                <p className="text-sm text-gray-500">No representatives available. Add representative contact details here.</p>
              ) : (
                <div className="space-y-4">
                  {(formData.representatives || []).map((rep, index) => (
                    <div key={`${rep.id ?? index}-${rep.contact_person}`} className="border border-gray-200 rounded p-4 bg-white">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-800">Representative #{index + 1}</h4>
                          {rep.id && (
                            <p className="text-xs text-gray-500">ID: {rep.id}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeRepresentativeField(index)}
                          className="text-red-600 hover:text-red-700 text-xs font-medium flex items-center gap-1"
                          disabled={loading}
                        >
                          <FaTrash className="w-3 h-3" />
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Contact Person <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaUser className="w-3 h-3 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              value={rep.contact_person || ""}
                              onChange={(event) => updateRepresentative(index, "contact_person", event.target.value)}
                              placeholder="Enter contact person's name"
                              className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_person
                                ? "border-red-500 bg-red-50"
                                : "border-gray-300"
                                }`}
                              disabled={loading}
                            />
                          </div>
                          {representativeErrors[index]?.contact_person && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.contact_person}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Contact Number <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-2">
                            <div className="w-32">
                              <SearchableSelect
                                value={selectedCountryCodes[index] || countryOptions.find(c => c.value === (rep.country_code || "+91")) || null}
                                onChange={(option: any) => updateRepresentativeCountryCode(index, option)}
                                options={countryOptions}
                                placeholder="Code"
                                disabled={loading}
                              />
                            </div>
                            <div className="flex-1 relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaPhone className="w-3 h-3 text-gray-400" />
                              </div>
                              <input
                                type="tel"
                                value={rep.contact_number || ""}
                                onChange={(e) => updateRepresentative(index, "contact_number", e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="Enter contact number"
                                className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_number
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                                  }`}
                                disabled={loading}
                              />
                            </div>
                          </div>
                          {representativeErrors[index]?.contact_number && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.contact_number}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaEnvelope className="w-3 h-3 text-gray-400" />
                            </div>
                            <input
                              type="email"
                              value={rep.email ?? ""}
                              onChange={(event) => updateRepresentative(index, "email", event.target.value)}
                              placeholder="Enter email (optional)"
                              className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.email ? "border-red-500 bg-red-50" : "border-gray-300"
                                }`}
                              disabled={loading}
                            />
                          </div>
                          {representativeErrors[index]?.email && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.email}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Designation <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            value={selectedDesignations[index] || (rep.designation_id ? designationOptions.find(d => d.value === rep.designation_id) : null) || null}
                            onChange={(option: any) => updateRepresentativeDesignation(index, option)}
                            options={designationOptions}
                            placeholder="Select designation"
                            disabled={loading}
                            isLoading={designationLoading}
                            onSearch={handleDesignationSearch}
                            // error={representativeErrors[index]?.designation_id}
                          />
                          {/* {representativeErrors[index]?.designation_id && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.designation_id}</p>
                          )} */}
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Location <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaMapMarkerAlt className="w-3 h-3 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              value={rep.representative_location || ""}
                              onChange={(event) => updateRepresentative(index, "representative_location", event.target.value)}
                              placeholder="Enter location"
                              className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.representative_location
                                ? "border-red-500 bg-red-50"
                                : "border-gray-300"
                                }`}
                              disabled={loading}
                            />
                          </div>
                          {representativeErrors[index]?.representative_location && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.representative_location}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading || uploading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || uploading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Uploading...
              </>
            ) : loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Saving...
              </>
            ) : (
              <>
                <FaBuilding className="w-4 h-4" />
                Update Vendor
              </>
            )}
          </button>
        </div>
      </div>
    </>
  )
}